import { useCallback, useEffect, useState } from "react";
import { selectCompany } from "../../../store/slices/authSlice";
import { useAppSelector } from "../../../store/hooks";

/**
 * Per-company lista av "aktiva" säljare — de vi vill se i dashboarden.
 * Sparas i localStorage så att säljchefen slipper välja om varje gång.
 *
 * Nyckeln är scope:ad per företag eftersom Dalakraft och Enkla har olika
 * säljteam och vi inte vill att en användare som byter bolag ska få med
 * sig gamla val.
 *
 * Tom lista = "inga val sparade än" → dashboarden faller tillbaka på att
 * visa alla säljare (genom att inte skicka userNos till backend).
 */
const STORAGE_KEY_PREFIX = "sales-manager:active-salespeople";

const buildStorageKey = (company: string | null): string | null =>
  company ? `${STORAGE_KEY_PREFIX}:${company}` : null;

const readFromStorage = (key: string | null): number[] => {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Filtrera bort skräp — bara numbers får passera.
    return parsed.filter((v): v is number => typeof v === "number");
  } catch {
    return [];
  }
};

const writeToStorage = (key: string | null, value: number[]): void => {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage kan kasta om quota är full — fortsätt ändå, state
    // hålls i minnet så sessionen fungerar även om persistansen fallerar.
  }
};

export interface UseActiveSalespeopleReturn {
  activeUserNos: number[];
  setActiveUserNos: (ids: number[]) => void;
  toggleUserNo: (id: number) => void;
  clear: () => void;
  isReady: boolean;
}

export const useActiveSalespeople = (): UseActiveSalespeopleReturn => {
  const company = useAppSelector(selectCompany);
  const storageKey = buildStorageKey(company);

  const [activeUserNos, setActiveState] = useState<number[]>(() =>
    readFromStorage(storageKey)
  );

  // När företaget byts (användaren loggar in som annan bolagsanvändare)
  // läs om från localStorage med den nya nyckeln.
  useEffect(() => {
    setActiveState(readFromStorage(storageKey));
  }, [storageKey]);

  const setActiveUserNos = useCallback(
    (ids: number[]) => {
      // Dedup och sortera för stabil persistans — gör jämförelser och
      // debugging enklare när man tittar i dev tools.
      const normalized = Array.from(new Set(ids)).sort((a, b) => a - b);
      setActiveState(normalized);
      writeToStorage(storageKey, normalized);
    },
    [storageKey]
  );

  const toggleUserNo = useCallback(
    (id: number) => {
      setActiveState((prev) => {
        const next = prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id].sort((a, b) => a - b);
        writeToStorage(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const clear = useCallback(() => {
    setActiveState([]);
    writeToStorage(storageKey, []);
  }, [storageKey]);

  return {
    activeUserNos,
    setActiveUserNos,
    toggleUserNo,
    clear,
    isReady: storageKey !== null,
  };
};
